import { useMemo } from 'react'

import { useSubscription } from '@apollo/client'
import { keepPreviousData } from '@tanstack/react-query'
import {
  AICAValueMetricsInput,
  AicaValueMetricsQuery,
  AicaValueMetricsQueryVariables,
  AIChatResponseEventFragment,
  AIChatTopQuestionsInput,
  AIChatTopQuestionsQuery,
  AIChatTopQuestionsQueryVariables,
  AICommunityAssistantUsageInput,
  AICommunityAssistantUsageQuery,
  AICommunityAssistantUsageQueryVariables,
  AIROIDataInput,
  CreateAIChatMutation,
  CreateAIChatMutationVariables,
  GetAIChatROIDataQuery,
  GetAIChatROIDataQueryVariables,
  LogAIChatDisabledOrgAttemptMutation,
  LogAIChatDisabledOrgAttemptMutationVariables,
  SearchAIChats,
  SearchAIChatsQuery,
  SearchAIChatsVariables,
  SubscribeToAIChatSessionResponses,
  SubscribeToAIChatSessionResponsesVariables,
  UpdateAIChatMutation,
  UpdateAIChatMutationVariables,
} from 'types/graphql'

import { MillisecondsInHour } from 'src/constants'
import { monitoring } from 'src/utils/monitoring'
import { QueryKey, useMutation, useQuery } from 'src/utils/queryClient'

const CreateAIChat = gql`
  mutation CreateAIChatMutation($input: CreateAIChatInput!) {
    createAIChat(input: $input) {
      id
      response
      questionCreatedAt
      responseCreatedAt
      citations {
        ...AIChatCitationFragment
      }
    }
  }
`

export const useCreateAIChatMutation = () => {
  return useMutation<CreateAIChatMutation, CreateAIChatMutationVariables>({
    mutationDocument: CreateAIChat,
  })
}

const UpdateAIChat = gql`
  mutation UpdateAIChatMutation($id: Int!, $input: UpdateAIChatInput!) {
    updateAIChat(id: $id, input: $input) {
      id
    }
  }
`

export const useUpdateAIChatMutation = () => {
  return useMutation<UpdateAIChatMutation, UpdateAIChatMutationVariables>({
    mutationDocument: UpdateAIChat,
  })
}

const LogAIChatDisabledOrgAttempt = gql`
  mutation LogAIChatDisabledOrgAttemptMutation($input: LogAIChatDisabledOrgAttemptInput!) {
    logAIChatDisabledOrgAttempt(input: $input)
  }
`

export const useLogAIChatDisabledOrgAttemptMutation = () => {
  return useMutation<
    LogAIChatDisabledOrgAttemptMutation,
    LogAIChatDisabledOrgAttemptMutationVariables
  >({
    mutationDocument: LogAIChatDisabledOrgAttempt,
  })
}

const AIROI = gql`
  query GetAIChatROIDataQuery($input: AIROIDataInput!) {
    aiChatROIData(input: $input) {
      activeUserCount
      afterHoursQuestions
      fallbackCount
      fallbackRate
      numberOfAIChats
      percentAfterHours
      percentOfAIChatsWithSubstantiveAnswer
      percentOfAIChatsWithThumbsUp
      inProductQuestions
      portalQuestions
      questionsAnsweredAutomatically
      thumbsDownCount
      thumbsUpCount
      totalFeedbackCount
      totalSessions
      widgetQuestions
    }
  }
`

export const useAIROIQuery = (input: AIROIDataInput) => {
  const { data, ...rest } = useQuery<GetAIChatROIDataQuery, GetAIChatROIDataQueryVariables>({
    queryDocument: AIROI,
    queryKey: [QueryKey.AIChatROIData, input],
    variables: { input },
    enabled: !!input.organizationId && !!input.startDate && !!input.endDate,
  })

  const aiChatROIData = useMemo(() => {
    return data?.aiChatROIData
  }, [data])

  return {
    ...rest,
    aiChatROIData,
  }
}

const AICommunityAssistantUsage = gql`
  query AICommunityAssistantUsageQuery($input: AICommunityAssistantUsageInput!) {
    aiCommunityAssistantUsage(input: $input) {
      tiers {
        id
        name
        isCurrent
        isDisabled
        isPopular
        priceText
        priceDetails
        allowedResponsesText
        buttonType
        buttonText
      }
      usedResponses
      quotaDetails {
        status
        usedResponses
        allowedResponses
        percentUsed
      }
    }
  }
`

export const useAICommunityAssistantUsageQuery = (input: AICommunityAssistantUsageInput) => {
  const { data, ...rest } = useQuery<
    AICommunityAssistantUsageQuery,
    AICommunityAssistantUsageQueryVariables
  >({
    queryDocument: AICommunityAssistantUsage,
    queryKey: [QueryKey.AICommunityAssistantUsage, input],
    variables: { input },
    staleTime: 1 * MillisecondsInHour,
    enabled: !!input.organizationId,
  })
  const aiCommunityAssistantUsage = useMemo(() => data?.aiCommunityAssistantUsage, [data])
  return {
    ...rest,
    aiCommunityAssistantUsage,
  }
}

const Search = gql`
  query SearchAIChats($input: SearchAIChatsQuery!) {
    searchAIChats(input: $input) {
      data {
        ...AIAssistantChatFragment
      }
      count
    }
  }
`

export const useSearchAIChatsQuery = (args: SearchAIChatsQuery) => {
  const { data, ...rest } = useQuery<SearchAIChats, SearchAIChatsVariables>({
    placeholderData: keepPreviousData,
    queryKey: [QueryKey.SearchAIChats, args],
    queryDocument: Search,
    variables: {
      input: args,
    },
    enabled: !!args.organizationId,
  })
  const chats = useMemo(() => data?.searchAIChats?.data || [], [data])
  const count = useMemo(() => data?.searchAIChats?.count || 0, [data])
  return {
    ...rest,
    chats,
    count,
  }
}

const AIChatSessionResponses = gql`
  subscription SubscribeToAIChatSessionResponses($chatSessionStorageId: String!) {
    aiChatSessionResponses(chatSessionStorageId: $chatSessionStorageId) {
      ...AIChatResponseEventFragment
    }
  }
`

type UseSubscribeToAIChatSessionResponsesArgs = {
  chatSessionStorageId: string
  onResponseEvent: (event: AIChatResponseEventFragment) => void | Promise<void>
}

export const useSubscribeToAIChatSessionResponses = (
  args: UseSubscribeToAIChatSessionResponsesArgs
) => {
  const { chatSessionStorageId, onResponseEvent } = args
  return useSubscription<
    SubscribeToAIChatSessionResponses,
    SubscribeToAIChatSessionResponsesVariables
  >(AIChatSessionResponses, {
    variables: {
      chatSessionStorageId,
    },
    onData: async ({ data }) => {
      const event = data?.data?.aiChatSessionResponses
      if (event) {
        await onResponseEvent(event)
      }
    },
    onError: (e) => {
      monitoring.logError(e, { source: 'aiChat subscription' })
    },
    shouldResubscribe: true,
  })
}

const AIChatTopQuestions = gql`
  query AIChatTopQuestionsQuery($input: AIChatTopQuestionsInput!) {
    aiChatTopQuestions(input: $input) {
      activeDays
      totalDaysInRange
      totalQuestions
      sourceBreakdown {
        servicesPortal
        websiteWidget
        inApp
      }
      topQuestions {
        id
        question
        response
        score
        questionCreatedAt
        pageSource
        citationCount
      }
    }
  }
`

export const useAIChatTopQuestionsQuery = (input: AIChatTopQuestionsInput) => {
  const { data, ...rest } = useQuery<AIChatTopQuestionsQuery, AIChatTopQuestionsQueryVariables>({
    queryDocument: AIChatTopQuestions,
    queryKey: [QueryKey.AIChatTopQuestions, input],
    variables: { input },
    staleTime: 24 * MillisecondsInHour,
    enabled: !!input.organizationId && !!input.startDate && !!input.endDate,
  })

  const topQuestionsResult = useMemo(() => data?.aiChatTopQuestions, [data])

  return {
    ...rest,
    topQuestionsResult,
  }
}

const AICAValueMetrics = gql`
  query AicaValueMetricsQuery($input: AICAValueMetricsInput!) {
    aicaValueMetrics(input: $input) {
      complexity {
        totalQuestions
        simpleQuestions
        complexQuestions
        complexPercentage
      }
      departmental {
        topDepartments {
          topicName
          questionCount
          percentageOfTotal
        }
        crossDepartmentQuestions
        crossDepartmentPercentage
        totalTopicsCovered
      }
      timeDeflection {
        estimatedMinutesSavedSimple
        estimatedMinutesSavedComplex
        totalEstimatedMinutesSaved
        totalEstimatedHoursSaved
        estimatedCostSavings
        questionsAnswered
        complexQuestionsAnswered
      }
      trust {
        totalAnswersWithCitations
        percentAnswersWithCitations
        totalCitations
        averageCitationsPerAnswer
        topCitedSources {
          sourceName
          sourceType
          citationCount
        }
      }
      executive {
        headlineMetric {
          label
          value
          description
        }
        periodComparison {
          currentPeriodValue
          previousPeriodValue
          changeAmount
          changePercentage
          trend
        }
        keyHighlights
        complexQuestionsResolved
        totalQuestionsAnswered
        estimatedTimeSavedHours
        topDepartment
      }
    }
  }
`

export const useAICAValueMetricsQuery = (input: AICAValueMetricsInput) => {
  const { data, ...rest } = useQuery<AicaValueMetricsQuery, AicaValueMetricsQueryVariables>({
    queryDocument: AICAValueMetrics,
    queryKey: [QueryKey.AICAValueMetrics, input],
    variables: { input },
    enabled: !!input.organizationId && !!input.startDate && !!input.endDate,
  })

  const valueMetrics = useMemo(() => data?.aicaValueMetrics, [data])

  return {
    ...rest,
    valueMetrics,
  }
}
